import React, { useState, useEffect, useCallback } from 'react';
import { patientActivitiesService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { Ico, emo } from '../ui/Ico';

const BRAND  = '#D4A574';
const BRAND2 = '#b8895c';

/* ── Helpers de data ─────────────────────────────────────────── */
const padZ  = (n) => String(n).padStart(2, '0');
const fmtMonthLabel = (ym) => {
  const [y, m] = ym.split('-');
  const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
};
const prevMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${padZ(m - 1)}`;
};
const nextMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${padZ(m + 1)}`;
};
const todayYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${padZ(d.getMonth() + 1)}`;
};
const daysInMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m, 0).getDate();
};
const firstWeekday = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).getDay(); // 0=Sun
};

/* ── Estilos ─────────────────────────────────────────────────── */
const S = {
  card:   { background: '#ffffff', border: '1.5px solid #ebe3da', borderRadius: '12px', padding: '1rem 1.1rem', marginBottom: '0.75rem' },
  label:  { display: 'block', fontSize: '0.73rem', fontWeight: 600, color: '#736b64', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:  { width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #ebe3da', borderRadius: '8px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  select: { width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #ebe3da', borderRadius: '8px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#ffffff' },
  btn:    { padding: '0.5rem 1.1rem', background: BRAND, color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' },
  btnOut: { padding: '0.5rem 1.1rem', background: '#ffffff', color: BRAND, border: `1.5px solid ${BRAND}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' },
};

/* ── Cores de adesão do calendário ───────────────────────────── */
const ADHERENCE_COLORS = {
  full:    '#2f5236', // verde escuro
  partial: '#6f9a77', // verde claro
  low:     '#c4933f', // laranja
  none:    '#ebe3da', // cinza
};

/* ── Ícones por tipo ─────────────────────────────────────────── */
const TYPE_ICONS = { meal: '✅', exercise: '🏃', weight: '⚖️', note: '📝' };
const TYPE_LABELS = { meal: 'Refeição', exercise: 'Exercício', weight: 'Peso', note: 'Nota' };
const TYPE_COLORS = {
  meal:     { bg: '#edf4ee', border: '#b9d2bd', icon: '#2f5236' },
  exercise: { bg: '#edf1f6', border: '#b8c7d8', icon: '#4f6a86' },
  weight:   { bg: '#edf1f6', border: '#b8c7d8', icon: '#7189a6' },
  note:     { bg: '#fbf8f5', border: '#c4933f', icon: '#8a5f1a' },
};

/* ── Modal genérico ──────────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onClick={onClose}>
      <div style={{ background: '#ffffff', borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{emo(title)}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#736b64', cursor: 'pointer', fontSize: '1.1rem' }}><Ico e="✕" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Calendário mensal ───────────────────────────────────────── */
function MonthCalendar({ month, activities, onDayClick }) {
  const days    = daysInMonth(month);
  const startWd = firstWeekday(month);
  const [y, m]  = month.split('-');

  // Mapear atividades por dia
  const byDay = {};
  for (const a of activities) {
    const day = parseInt(a.activity_date.split('-')[2], 10);
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(a);
  }

  const getColor = (day) => {
    const acts = byDay[day];
    if (!acts || !acts.length) return ADHERENCE_COLORS.none;
    const meals = acts.filter(a => a.activity_type === 'meal').length;
    if (meals >= 5) return ADHERENCE_COLORS.full;
    if (meals >= 2) return ADHERENCE_COLORS.partial;
    if (acts.length > 0) return ADHERENCE_COLORS.low;
    return ADHERENCE_COLORS.none;
  };

  const today  = new Date();
  const todayD = today.getFullYear() === parseInt(y) && today.getMonth() + 1 === parseInt(m) ? today.getDate() : -1;

  const cells = [];
  for (let i = 0; i < startWd; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  return (
    <div>
      {/* Cabeçalho dias da semana */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
        {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#a39b93', padding: '0.25rem 0' }}>{d}</div>
        ))}
      </div>
      {/* Dias */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {cells.map((day, i) => (
          <div key={i}
            onClick={() => day && onDayClick(day, byDay[day] || [])}
            style={{
              aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '6px', fontSize: '0.78rem', fontWeight: day === todayD ? 800 : 500,
              cursor: day ? 'pointer' : 'default',
              background: day ? getColor(day) : 'transparent',
              color: day ? (getColor(day) === ADHERENCE_COLORS.none ? '#736b64' : '#ffffff') : 'transparent',
              border: day === todayD ? `2px solid ${BRAND}` : '2px solid transparent',
              transition: 'opacity 0.1s',
            }}
            onMouseEnter={e => { if (day) e.currentTarget.style.opacity = '0.8'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
          >
            {day || ''}
          </div>
        ))}
      </div>

      {/* Legenda */}
      <div style={{ display: 'flex', gap: '0.85rem', marginTop: '0.85rem', flexWrap: 'wrap' }}>
        {[
          { color: ADHERENCE_COLORS.full,    label: 'Adesão total (≥5 refeições)' },
          { color: ADHERENCE_COLORS.partial, label: 'Adesão parcial (2-4)' },
          { color: ADHERENCE_COLORS.low,     label: 'Baixa adesão' },
          { color: ADHERENCE_COLORS.none,    label: 'Sem registro' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <div style={{ width: 12, height: 12, borderRadius: '3px', background: color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.7rem', color: '#736b64' }}>{emo(label)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Card de atividade individual ────────────────────────────── */
function ActivityCard({ activity, onLike }) {
  const type  = activity.activity_type;
  const style = TYPE_COLORS[type] || TYPE_COLORS.note;

  const [liked, setLiked] = useState(activity.liked);
  const [liking, setLiking] = useState(false);

  const handleLike = async () => {
    setLiking(true);
    try {
      const newVal = !liked;
      await patientActivitiesService.toggleLike(activity.id, newVal);
      setLiked(newVal);
      if (onLike) onLike(activity.id, newVal);
    } catch { /* silent */ }
    finally { setLiking(false); }
  };

  const fmtDate = (d) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div style={{ background: style.bg, border: `1.5px solid ${style.border}`, borderRadius: '12px', padding: '0.85rem 1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
      <span style={{ fontSize: '1.3rem', flexShrink: 0, color: style.icon }}>{emo(TYPE_ICONS[type])}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'baseline', flexWrap: 'wrap', marginBottom: '0.2rem' }}>
          <span style={{ fontSize: '0.72rem', color: '#736b64', fontWeight: 600 }}>{fmtDate(activity.activity_date)}</span>
          {activity.activity_time && (
            <span style={{ fontSize: '0.7rem', color: '#d9d0c6' }}>às {activity.activity_time.slice(0,5)}</span>
          )}
          <span style={{ fontSize: '0.7rem', background: style.border + '40', color: style.icon, padding: '0 5px', borderRadius: '8px', fontWeight: 600 }}>{emo(TYPE_LABELS[type])}</span>
        </div>

        {/* Descrição */}
        {type === 'meal' && (
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', color: '#2f2a26', fontWeight: 600 }}>
            Cumpriu {activity.meal_name || 'refeição'}{activity.activity_time ? ` às ${activity.activity_time.slice(0,5)}` : ''}
          </p>
        )}
        {type === 'exercise' && (
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', color: '#2f2a26', fontWeight: 600 }}>{activity.description}</p>
        )}
        {type === 'weight' && (
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', color: '#2f2a26', fontWeight: 600 }}>{activity.weight_kg} kg</p>
        )}
        {type === 'note' && (
          <p style={{ margin: '0 0 0.4rem', fontSize: '0.85rem', color: '#4a4440' }}>{activity.description}</p>
        )}

        {/* Badges de métricas */}
        {type === 'exercise' && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {activity.duration_minutes != null && (
              <span style={{ background: '#edf1f6', color: '#4f6a86', fontSize: '0.72rem', fontWeight: 600, padding: '1px 7px', borderRadius: '10px' }}><Ico e="⏱" /> {activity.duration_minutes} min</span>
            )}
            {activity.calories != null && (
              <span style={{ background: '#f8f1e2', color: '#8a5f1a', fontSize: '0.72rem', fontWeight: 600, padding: '1px 7px', borderRadius: '10px' }}><Ico e="🔥" /> {activity.calories} kcal</span>
            )}
            {activity.distance_km != null && (
              <span style={{ background: '#edf4ee', color: '#2f5236', fontSize: '0.72rem', fontWeight: 600, padding: '1px 7px', borderRadius: '10px' }}><Ico e="📍" /> {activity.distance_km} km</span>
            )}
          </div>
        )}
      </div>

      {/* Botão curtir */}
      <button onClick={handleLike} disabled={liking}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, flexShrink: 0, color: liked ? BRAND : '#d9d0c6', transition: 'color 0.2s', padding: '2px' }}
        title={liked ? 'Descurtir' : 'Curtir'}>
        {emo(liked ? '❤️' : '🤍')}
      </button>
    </div>
  );
}

/* ── Formulário de nova atividade ────────────────────────────── */
function NovaAtividadeForm({ patientId, onSaved, onClose }) {
  const [type,    setType]    = useState('exercise');
  const [form,    setForm]    = useState({
    description: '', meal_name: '', weight_kg: '', calories: '',
    duration_minutes: '', distance_km: '', activity_date: new Date().toISOString().slice(0,10),
    activity_time: '',
  });
  const [saving, setSaving]  = useState(false);
  const S2 = {
    label:  { display: 'block', fontSize: '0.73rem', fontWeight: 600, color: '#736b64', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' },
    input:  { width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #ebe3da', borderRadius: '8px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
    select: { width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #ebe3da', borderRadius: '8px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#ffffff' },
  };

  const fld = (key) => ({ value: form[key], onChange: e => setForm(f => ({ ...f, [key]: e.target.value })) });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        patient_id:   patientId,
        activity_type: type,
        activity_date: form.activity_date,
        activity_time: form.activity_time || null,
        description:   form.description,
        meal_name:     form.meal_name,
        weight_kg:     form.weight_kg     ? parseFloat(form.weight_kg)     : null,
        calories:      form.calories      ? parseInt(form.calories)        : null,
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
        distance_km:   form.distance_km   ? parseFloat(form.distance_km)   : null,
      };
      const res = await patientActivitiesService.create(payload);
      if (res.success) { onSaved(); onClose(); }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
      <div>
        <label style={S2.label}>Tipo</label>
        <select style={S2.select} value={type} onChange={e => setType(e.target.value)}>
          <option value="exercise">Exercício</option>
          <option value="meal">Refeição</option>
          <option value="weight">Peso</option>
          <option value="note">Nota</option>
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
        <div>
          <label style={S2.label}>Data *</label>
          <input type="date" style={S2.input} {...fld('activity_date')} />
        </div>
        <div>
          <label style={S2.label}>Horário</label>
          <input type="time" style={S2.input} {...fld('activity_time')} />
        </div>
      </div>

      {type === 'exercise' && (
        <>
          <div>
            <label style={S2.label}>Descrição do exercício *</label>
            <input style={S2.input} {...fld('description')} placeholder="Ex: Corrida, Musculação..." autoFocus />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
            <div><label style={S2.label}>Duração (min)</label><input type="number" style={S2.input} {...fld('duration_minutes')} placeholder="45" /></div>
            <div><label style={S2.label}>Energia (kcal)</label><input type="number" style={S2.input} {...fld('calories')} placeholder="300" /></div>
            <div><label style={S2.label}>Distância (km)</label><input type="number" step="0.1" style={S2.input} {...fld('distance_km')} placeholder="5" /></div>
          </div>
        </>
      )}
      {type === 'meal' && (
        <div>
          <label style={S2.label}>Nome da refeição *</label>
          <input style={S2.input} {...fld('meal_name')} placeholder="Ex: Café da manhã, Almoço..." autoFocus />
        </div>
      )}
      {type === 'weight' && (
        <div>
          <label style={S2.label}>Peso (kg) *</label>
          <input type="number" step="0.1" style={S2.input} {...fld('weight_kg')} placeholder="Ex: 68.5" autoFocus />
        </div>
      )}
      {type === 'note' && (
        <div>
          <label style={S2.label}>Anotação *</label>
          <textarea style={{ ...S2.input, minHeight: '80px', resize: 'vertical' }} {...fld('description')} placeholder="Observação livre..." autoFocus />
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
        <button style={{ padding: '0.5rem 1.1rem', background: '#ffffff', color: BRAND, border: `1.5px solid ${BRAND}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }} onClick={onClose}>Cancelar</button>
        <button style={{ padding: '0.5rem 1.1rem', background: BRAND, color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }} onClick={handleSave} disabled={saving}>
          {saving ? 'Salvando...' : 'Registrar'}
        </button>
      </div>
    </div>
  );
}

/* ── Componente principal ────────────────────────────────────── */
export default function TabAcompanhamento({ patientId }) {
  const { showError } = useToast();
  const [month,      setMonth]      = useState(todayYM());
  const [activities, setActivities] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [showModal,  setShowModal]  = useState(false);
  const [dayModal,   setDayModal]   = useState(null); // { day, acts }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await patientActivitiesService.getByMonth(patientId, month);
      if (res.success) setActivities(res.data || []);
      else showError('Erro ao carregar atividades');
    } catch { showError('Erro ao carregar atividades'); }
    finally { setLoading(false); }
  }, [patientId, month, showError]);

  useEffect(() => { load(); }, [load]);

  const filtered = filterType === 'all'
    ? activities
    : activities.filter(a => a.activity_type === filterType);

  return (
    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>

      {/* ── Coluna esquerda: calendário ── */}
      <div style={{ flex: '0 0 300px', minWidth: 0 }}>
        <div style={S.card}>
          {/* Navegação de mês */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <button onClick={() => setMonth(prevMonth(month))}
              style={{ background: 'none', border: '1.5px solid #ebe3da', borderRadius: '7px', padding: '0.3rem 0.6rem', cursor: 'pointer', color: '#736b64', fontSize: '0.9rem' }}>←</button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2f2a26' }}>{fmtMonthLabel(month)}</div>
              {month !== todayYM() && (
                <button onClick={() => setMonth(todayYM())}
                  style={{ background: 'none', border: 'none', color: BRAND, fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                  Hoje
                </button>
              )}
            </div>
            <button onClick={() => setMonth(nextMonth(month))}
              style={{ background: 'none', border: '1.5px solid #ebe3da', borderRadius: '7px', padding: '0.3rem 0.6rem', cursor: 'pointer', color: '#736b64', fontSize: '0.9rem' }}>→</button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#a39b93', fontSize: '0.85rem' }}>Carregando...</div>
          ) : (
            <MonthCalendar month={month} activities={activities}
              onDayClick={(day, acts) => setDayModal({ day, acts })} />
          )}
        </div>

        {/* Resumo do mês */}
        <div style={S.card}>
          <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Resumo do mês</h4>
          {[
            { type: 'meal',     label: 'Refeições registradas' },
            { type: 'exercise', label: 'Treinos'               },
            { type: 'weight',   label: 'Pesagens'              },
            { type: 'note',     label: 'Notas'                 },
          ].map(({ type, label }) => {
            const count = activities.filter(a => a.activity_type === type).length;
            return (
              <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0', borderBottom: '1px solid #f6f0ea' }}>
                <span style={{ fontSize: '0.82rem', color: '#4a4440' }}>{emo(TYPE_ICONS[type])} {emo(label)}</span>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: count > 0 ? BRAND : '#d9d0c6' }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Coluna direita: feed de atividades ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <select style={{ ...S.select, width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.82rem' }}
            value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">Todas as atividades</option>
            <option value="meal">Refeições</option>
            <option value="exercise">Exercícios</option>
            <option value="weight">Peso</option>
            <option value="note">Notas</option>
          </select>
          <button style={S.btn} onClick={() => setShowModal(true)}>+ Registrar atividade</button>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '2rem', color: '#a39b93' }}>Carregando...</div>}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#a39b93', fontSize: '0.85rem' }}>
            Nenhuma atividade registrada em {fmtMonthLabel(month)}.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {filtered.map(act => (
            <ActivityCard key={act.id} activity={act} onLike={(id, liked) => {
              setActivities(prev => prev.map(a => a.id === id ? { ...a, liked } : a));
            }} />
          ))}
        </div>
      </div>

      {/* ── Modal registrar atividade ── */}
      {showModal && (
        <Modal title="Registrar Atividade" onClose={() => setShowModal(false)}>
          <NovaAtividadeForm patientId={patientId} onSaved={load} onClose={() => setShowModal(false)} />
        </Modal>
      )}

      {/* ── Modal detalhe do dia ── */}
      {dayModal && (
        <Modal title={`Dia ${dayModal.day}`} onClose={() => setDayModal(null)}>
          {dayModal.acts.length === 0 ? (
            <p style={{ color: '#736b64', fontSize: '0.85rem' }}>Nenhuma atividade registrada neste dia.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
              {dayModal.acts.map(act => (
                <ActivityCard key={act.id} activity={act} onLike={(id, liked) => {
                  setActivities(prev => prev.map(a => a.id === id ? { ...a, liked } : a));
                  setDayModal(prev => prev ? { ...prev, acts: prev.acts.map(a => a.id === id ? { ...a, liked } : a) } : null);
                }} />
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
